import type { ReactNode } from 'react'
import { useState } from 'react'

import {
  ResponsiveDialogBody,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'

import locales from '../locales'
import type { UsageProfile } from '../plan-finder.utils'
import { RadioOptionCard } from '../radio-option-card'

interface UsageStepProps {
  progressBar: ReactNode
  usageProfile: UsageProfile
  onSelectUsage: (profile: UsageProfile) => void
  onBack: () => void
  onNext: () => void
}

export function UsageStep({
  progressBar,
  usageProfile,
  onSelectUsage,
  onBack,
  onNext,
}: UsageStepProps) {
  const { t } = useComponentTranslation(locales)
  const [showSelectError, setShowSelectError] = useState(false)

  const handleSelect = (profile: UsageProfile) => {
    setShowSelectError(false)
    onSelectUsage(profile)
  }

  const handleNext = () => {
    if (!usageProfile) {
      setShowSelectError(true)
      return
    }
    onNext()
  }

  return (
    <>
      <ResponsiveDialogHeader>
        <ResponsiveDialogTitle>{t('title')}</ResponsiveDialogTitle>
        <ResponsiveDialogDescription>
          {t('steps.description')}
        </ResponsiveDialogDescription>
      </ResponsiveDialogHeader>

      <ResponsiveDialogBody>
        <div className="space-y-4">
          {progressBar}

          <p className="text-sm font-medium">{t('steps.usage.question')}</p>

          <div className="space-y-2">
            <RadioOptionCard
              selected={usageProfile === 'uniform'}
              onClick={() => handleSelect('uniform')}
            >
              <div>
                <span className="font-medium">{t('steps.usage.uniform')}</span>
                <p className="text-muted-foreground text-xs">
                  {t('steps.usage.uniformHint')}
                </p>
              </div>
            </RadioOptionCard>

            <RadioOptionCard
              selected={usageProfile === 'seasonal'}
              onClick={() => handleSelect('seasonal')}
            >
              <div>
                <span className="font-medium">{t('steps.usage.seasonal')}</span>
                <p className="text-muted-foreground text-xs">
                  {t('steps.usage.seasonalHint')}
                </p>
              </div>
            </RadioOptionCard>
          </div>

          {showSelectError && (
            <p role="alert" className="text-destructive text-xs font-medium">
              {t('steps.usage.selectError')}
            </p>
          )}
        </div>
      </ResponsiveDialogBody>

      <ResponsiveDialogFooter>
        <Button variant="outline" onClick={onBack}>
          {t('actions.back')}
        </Button>
        <Button onClick={handleNext}>{t('actions.next')}</Button>
      </ResponsiveDialogFooter>
    </>
  )
}
